import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Navbar from "../components/Navbar";
import LocationPicker from "../components/LocationPicker";
import "./css/CommonComplete.css";

const UAE_STATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

export default function ProfileStep2() {
  const navigate = useNavigate();
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [state, setState] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const [touched, setTouched] = useState({});
  const [throwError, setThrowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      step: "Step",
      addressDetails: "Address Details",
      subtitle: "Please fill out the address details below.",
      addressLine1: "Address Line 1 *",
      enterAddress1: "Enter address line 1",
      invalidAddress1: "Please enter a valid address line 1.",
      addressLine2: "Address Line 2",
      enterAddress2: "Enter address line 2 (optional)",
      invalidAddress2: "Please enter a valid address line 2.",
      city: "City *",
      enterCity: "Enter city",
      invalidCity: "Please enter a valid city.",
      pincode: "Pincode *",
      enterPincode: "Enter pincode",
      invalidPincode: "Please enter a valid pincode.",
      state: "State *",
      selectState: "Select state",
      invalidState: "Please select a state.",
      locationCoordinates: "Location Coordinates *",
      latitude: "Latitude",
      longitude: "Longitude",
      enterLatitude: "Enter latitude",
      enterLongitude: "Enter longitude",
      invalidCoordinates: "Please enter valid location coordinates.",
      getCurrentLocation: "Get Current Location",
      gettingLocation: "Getting location...",
      selectLocationOnMap: "Select Location on Map",
      locationSelected: "Location Selected",
      selectLocationRequired: "Please select a location on the map.",
      continue: "Continue",
      failedToLoad: "Failed to load profile data",
      somethingWrong: "Something went wrong!",
      locationError: "Unable to get your location. Please enter coordinates manually.",
      locationPermissionDenied: "Location permission denied. Please enable location access.",
    },
    ar: {
      loading: "جاري التحميل",
      step: "الخطوة",
      addressDetails: "تفاصيل العنوان",
      subtitle: "يرجى ملء تفاصيل العنوان أدناه.",
      addressLine1: "عنوان السطر 1 *",
      enterAddress1: "أدخل عنوان السطر 1",
      invalidAddress1: "يرجى إدخال عنوان سطر 1 صحيح.",
      addressLine2: "عنوان السطر 2",
      enterAddress2: "أدخل عنوان السطر 2 (اختياري)",
      invalidAddress2: "يرجى إدخال عنوان سطر 2 صحيح.",
      city: "المدينة *",
      enterCity: "أدخل المدينة",
      invalidCity: "يرجى إدخال مدينة صحيحة.",
      pincode: "الرمز البريدي *",
      enterPincode: "أدخل الرمز البريدي",
      invalidPincode: "يرجى إدخال رمز بريدي صحيح.",
      state: "الولاية *",
      selectState: "اختر الولاية",
      invalidState: "يرجى اختيار ولاية.",
      locationCoordinates: "إحداثيات الموقع *",
      latitude: "خط العرض",
      longitude: "خط الطول",
      enterLatitude: "أدخل خط العرض",
      enterLongitude: "أدخل خط الطول",
      invalidCoordinates: "يرجى إدخال إحداثيات موقع صحيحة.",
      getCurrentLocation: "الحصول على الموقع الحالي",
      gettingLocation: "جاري الحصول على الموقع...",
      selectLocationOnMap: "اختر الموقع على الخريطة",
      locationSelected: "تم اختيار الموقع",
      selectLocationRequired: "يرجى اختيار موقع على الخريطة.",
      continue: "متابعة",
      failedToLoad: "فشل تحميل بيانات الملف الشخصي",
      somethingWrong: "حدث خطأ ما!",
      locationError: "تعذر الحصول على موقعك. يرجى إدخال الإحداثيات يدوياً.",
      locationPermissionDenied: "تم رفض إذن الموقع. يرجى تفعيل الوصول إلى الموقع.",
    },
  }[lang];

  // Validators (defined early for use in profile fetch)
  const validateLatitude = (lat) => {
    const num = Number(lat);
    return !isNaN(num) && num >= -90 && num <= 90;
  };
  const validateLongitude = (lng) => {
    const num = Number(lng);
    return !isNaN(num) && num >= -180 && num <= 180;
  };

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setInitialLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profile = data.response;
          
          // Set language preference from API response to localStorage
          if (profile.language_preference) {
            localStorage.setItem("app_language", profile.language_preference);
            setLang(profile.language_preference);
          }

          // Navigation logic based on profile_completed_step and is_verified
          const completedStep = profile.profile_completed_step || 0;
          
          // If onboarding is complete, navigate to dashboard
          if (completedStep === 5) {
            navigate("/dashboard");
            return;
          }
          
          // If verified, redirect from steps 1-3 to step 4
          const currentStep = 2;
          if (profile.is_verified === true && currentStep <= 3) {
            navigate("/profile-step-4");
            return;
          }
          
          // If current step is ahead of completed step + 1, redirect to appropriate step
          if (currentStep > completedStep + 1) {
            const nextStep = completedStep + 1;
            navigate(`/profile-step-${nextStep}`);
            return;
          }
          
          // Populate form fields if data is present
          if (profile.address1) setAddress1(profile.address1);
          if (profile.address2) setAddress2(profile.address2);
          if (profile.city) setCity(profile.city);
          if (profile.pincode) setPincode(profile.pincode.toString());
          if (profile.state) setState(profile.state);
          
          // Handle location coordinates
          // location_coordinates is [longitude, latitude] format
          if (profile.location_coordinates && Array.isArray(profile.location_coordinates) && profile.location_coordinates.length === 2) {
            const lng = profile.location_coordinates[0];
            const lat = profile.location_coordinates[1];
            
            // Validate coordinates from API before setting
            if (validateLatitude(lat) && validateLongitude(lng)) {
              setLatitude(lat.toString());
              setLongitude(lng.toString());
            } else {
              // Invalid coordinates from API - log warning but don't set them
              console.warn("Invalid coordinates received from API:", lat, lng);
              // Optionally show error to user
              const currentLang = localStorage.getItem("app_language") || "en";
              const translations = {
                en: { invalidCoordinates: "Please enter valid location coordinates." },
                ar: { invalidCoordinates: "يرجى إدخال إحداثيات موقع صحيحة." },
              };
              showToast(translations[currentLang].invalidCoordinates, "error");
            }
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        // Don't show error toast if profile doesn't exist (404) - user might be creating new profile
        if (err.code !== 404) {
          const currentLang = localStorage.getItem("app_language") || "en";
          const translations = {
            en: { failedToLoad: "Failed to load profile data" },
            ar: { failedToLoad: "فشل تحميل بيانات الملف الشخصي" },
          };
          showToast(err.message || translations[currentLang].failedToLoad, "error");
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfile();
  }, [USER_BASE_URL, VERSION, navigate]);

  // Get current location using Geolocation API
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      showToast(t.locationError, "error");
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Validate coordinates from geolocation
        if (!validateLatitude(lat) || !validateLongitude(lng)) {
          setGettingLocation(false);
          showToast(t.invalidCoordinates, "error");
          return;
        }
        
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        setGettingLocation(false);
        setTouched((t) => ({ ...t, latitude: true, longitude: true }));
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          showToast(t.locationPermissionDenied, "error");
        } else {
          showToast(t.locationError, "error");
        }
      }
    );
  };

  // Handle location selection from map picker
  const handleLocationSelect = (lat, lng) => {
    // Validate coordinates before setting
    if (!validateLatitude(lat) || !validateLongitude(lng)) {
      showToast(t.invalidCoordinates, "error");
      return;
    }
    setLatitude(lat.toFixed(6));
    setLongitude(lng.toFixed(6));
    setTouched((t) => ({ ...t, latitude: true, longitude: true }));
  };

  // Validators
  const validateAddress1 = (addr) => addr.trim().length >= 1 && addr.trim().length <= 255;
  const validateAddress2 = (addr) => addr.trim().length === 0 || (addr.trim().length >= 1 && addr.trim().length <= 255);
  const validateCity = (city) => city.trim().length >= 1 && city.trim().length <= 100;
  const validatePincode = (pin) => {
    const num = Number(pin);
    return !isNaN(num) && num > 0;
  };
  const validateState = (state) => UAE_STATES.includes(state);

  const validateInputs = () => {
    if (
      !validateAddress1(address1) ||
      !validateAddress2(address2) ||
      !validateCity(city) ||
      !validatePincode(pincode) ||
      !validateState(state)
    ) {
      setThrowError(true);
      return false;
    }

    // Validate location coordinates
    if (!latitude || !longitude) {
      setThrowError(true);
      showToast(t.selectLocationRequired, "error");
      return false;
    }

    if (!validateLatitude(latitude) || !validateLongitude(longitude)) {
      setThrowError(true);
      showToast(t.invalidCoordinates, "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setThrowError(false);
    if (!validateInputs()) return;

    setLoading(true);
    try {
      const body = {
        address1: address1.trim(),
        city: city.trim(),
        pincode: Number(pincode),
        state: state,
        // location_coordinates is [longitude, latitude] format
        location_coordinates: [Number(longitude), Number(latitude)],
      };

      if (address2.trim() !== "") {
        body.address2 = address2.trim();
      }

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/complete-profile/step-2`,
        "POST",
        JSON.stringify(body),
        { "Content-Type": "application/json" }
      );

      setLoading(false);
      if (data && data.response_code === 200) {
        showToast(data.message, "success");
        setTimeout(() => navigate("/profile-step-3"), 500);
      } else {
        const currentLang = localStorage.getItem("app_language") || "en";
        const translations = {
          en: { somethingWrong: "Something went wrong!" },
          ar: { somethingWrong: "حدث خطأ ما!" },
        };
        showToast(data.message || translations[currentLang].somethingWrong, "error");
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      showToast(err.message, "error");
    }
  };

  if (initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Navbar is_back_arrow={true} is_step_heading={true} heading={t.step} step_number={2} />

      <h2 className="title">{t.addressDetails}</h2>
      <p className="subtitle">{t.subtitle}</p>

      <form className="pc-form">
        {/* Address Line 1 */}
        <p className="form-field-title">{t.addressLine1}</p>
        <div className="input-field-div">
          <input
            type="text"
            placeholder={t.enterAddress1}
            maxLength={255}
            className="pc-input"
            value={address1}
            onChange={(e) => {
              setAddress1(e.target.value);
              setTouched((t) => ({ ...t, address1: true }));
            }}
          />
          {(touched.address1 || throwError) && !validateAddress1(address1) && (
            <div className="error">{t.invalidAddress1}</div>
          )}
        </div>

        {/* Address Line 2 */}
        <p className="form-field-title">{t.addressLine2}</p>
        <div className="input-field-div">
          <input
            type="text"
            placeholder={t.enterAddress2}
            maxLength={255}
            className="pc-input"
            value={address2}
            onChange={(e) => {
              setAddress2(e.target.value);
              setTouched((t) => ({ ...t, address2: true }));
            }}
          />
          {(touched.address2 || throwError) && !validateAddress2(address2) && (
            <div className="error">{t.invalidAddress2}</div>
          )}
        </div>

        {/* City */}
        <p className="form-field-title">{t.city}</p>
        <div className="input-field-div">
          <input
            type="text"
            placeholder={t.enterCity}
            maxLength={100}
            className="pc-input"
            value={city}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z\s]/g, "")
                .replace(/\s{2,}/g, " ");
              setCity(value);
              setTouched((t) => ({ ...t, city: true }));
            }}
          />
          {(touched.city || throwError) && !validateCity(city) && (
            <div className="error">{t.invalidCity}</div>
          )}
        </div>

        {/* Pincode */}
        <p className="form-field-title">{t.pincode}</p>
        <div className="input-field-div">
          <input
            type="text"
            inputMode="numeric"
            placeholder={t.enterPincode}
            className="pc-input"
            value={pincode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setPincode(value);
              setTouched((t) => ({ ...t, pincode: true }));
            }}
          />
          {(touched.pincode || throwError) && !validatePincode(pincode) && (
            <div className="error">{t.invalidPincode}</div>
          )}
        </div>

        {/* State */}
        <p className="form-field-title">{t.state}</p>
        <div className="input-field-div">
          <select
            className="pc-input"
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              setTouched((t) => ({ ...t, state: true }));
            }}
          >
            <option value="">{t.selectState}</option>
            {UAE_STATES.map((stateName) => (
              <option key={stateName} value={stateName}>
                {stateName}
              </option>
            ))}
          </select>
          {(touched.state || throwError) && !validateState(state) && (
            <div className="error">{t.invalidState}</div>
          )}
        </div>

        {/* Location Coordinates */}
        <p className="form-field-title">{t.locationCoordinates}</p>
        <div className="input-field-div">
          <button
            type="button"
            onClick={() => setShowLocationPicker(true)}
            className="location-btn"
            style={{
              width: "100%",
              padding: "12px",
              marginBottom: "10px",
              backgroundColor: latitude && longitude ? "#28a745" : "#47c0c9",
              color: "white",
              border: "none",
              borderRadius: "5px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background-color 0.3s",
            }}
          >
            {latitude && longitude ? t.locationSelected : t.selectLocationOnMap}
          </button>
          {latitude && longitude && (
            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={t.enterLatitude}
                  className="pc-input"
                  value={latitude}
                  readOnly
                  style={{ backgroundColor: "#f9fafb", cursor: "not-allowed" }}
                />
                <small style={{ fontSize: "12px", color: "#666" }}>{t.latitude}</small>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={t.enterLongitude}
                  className="pc-input"
                  value={longitude}
                  readOnly
                  style={{ backgroundColor: "#f9fafb", cursor: "not-allowed" }}
                />
                <small style={{ fontSize: "12px", color: "#666" }}>{t.longitude}</small>
              </div>
            </div>
          )}
          {(touched.latitude || touched.longitude || throwError) &&
            (!latitude || !longitude || !validateLatitude(latitude) || !validateLongitude(longitude)) && (
              <div className="error">{!latitude || !longitude ? t.selectLocationRequired : t.invalidCoordinates}</div>
            )}
          <button
            type="button"
            onClick={handleGetCurrentLocation}
            disabled={gettingLocation}
            style={{
              width: "100%",
              padding: "10px",
              marginTop: "10px",
              backgroundColor: "transparent",
              color: "#47c0c9",
              border: "1px solid #47c0c9",
              borderRadius: "5px",
              fontSize: "14px",
              cursor: gettingLocation ? "not-allowed" : "pointer",
              opacity: gettingLocation ? 0.7 : 1,
            }}
          >
            {gettingLocation ? t.gettingLocation : t.getCurrentLocation}
          </button>
        </div>
      </form>

      <button
        onClick={handleSubmit}
        type="submit"
        className="continue-btn"
        disabled={loading}
      >
        {loading ? <span className="loader"></span> : t.continue}
      </button>

      {/* Location Picker Modal */}
      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelect={handleLocationSelect}
        initialLat={latitude || null}
        initialLng={longitude || null}
        lang={lang}
      />
    </div>
  );
}

